import { Grid } from '@mui/material';
import { RoutalLoader, RoutalPalette } from '@nx-smartmonkey/ui';
import { useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';

import PageLayout from '../../../components/new-layout/PageLayout';
import * as routing from '../../../constants/Routing';
import { useRetrieveActiveProject } from '../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveActiveProjectGeoFences } from '../../../hooks/project/useRetrieveActiveProjectGeoFences';
import { useCustomParams } from '../../../hooks/useCustomParams';
import { PlanDetailsContextProvider } from './context';
import { CurrentOptimizationDialog } from './dialogs/CurrentOptimizationDialog';
import { OptimizationSnackbar } from './dialogs/OptimizationSnackbar';
import { useCleanActivePlan } from './hooks/plan-details/useCleanActivePlan';
import { useActivePlanWebSockets } from './hooks/plan-details/usePlanWebSockets';
import { useRetrieveActivePlan } from './hooks/plan-details/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from './hooks/plan-details/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanStops } from './hooks/plan-details/useRetrieveActivePlanStops';
import { PlanDriversList } from './plan-drivers/PlanDriversList';
import { PlanHeader } from './plan-header/PlanHeader';
import { Overview } from './plan-overview';
import { PlanStopsList } from './plan-stops/PlanStopsList';
import { PlanTasksList } from './plan-tasks/PlanTasksList';

export const PlanDetails = () => {
  return (
    <PlanDetailsContextProvider>
      <PlanDetailsContent />
    </PlanDetailsContextProvider>
  );
};

const PlanDetailsContent = () => {
  const navigate = useNavigate();
  const { value: planId } = useCustomParams({ param: `planId` });

  useActivePlanWebSockets();
  const { run: cleanActivePlan } = useCleanActivePlan();

  const { data: activeProject } = useRetrieveActiveProject();
  const {
    data: activePlan,
    isLoading: activePlanLoading,
    run: retrieveActivePlan,
    error: activePlanError,
  } = useRetrieveActivePlan();

  const { run: retrieveActivePlanStops } = useRetrieveActivePlanStops();
  const { run: retrieveActivePlanDrivers } = useRetrieveActivePlanDrivers();
  const { run: retrieveActiveProjectGeoFences } = useRetrieveActiveProjectGeoFences();

  useEffect(() => {
    if (activePlanError) {
      navigate(`${routing.PLANNER.ROOT.replace(`:projectId`, activeProject?.id ?? ``)}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlanError]);

  useEffect(() => {
    if (planId !== activePlan?.id) {
      retrieveActivePlan();
      retrieveActivePlanStops();
      retrieveActivePlanDrivers();
      retrieveActiveProjectGeoFences();
    }
    return () => {
      cleanActivePlan();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!activeProject || activePlanLoading) {
    return (
      <Grid
        container
        justifyContent="center"
        style={{ height: `100%`, backgroundColor: RoutalPalette.neutral00 }}
        alignItems="center"
      >
        <Grid container justifyContent="center" style={{ width: `200px`, height: `200px` }}>
          <RoutalLoader />
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <PageLayout style={{ backgroundColor: RoutalPalette.neutral00 }}>
        <Grid container direction="column" wrap="nowrap">
          <PlanHeader activePlan={activePlan} isLoading={activePlanLoading} activeProject={activeProject} />

          <Routes>
            <Route path="/overview" element={<Overview />} />

            <>
              <Route path="/drivers" element={<PlanDriversList />} />

              <Route path="/stops" element={<PlanStopsList />} />
              <Route
                path="/tasks"
                element={
                  <PlanTasksList
                  // openTask={(stop: Stop, detailTab?: DetailTabTypes) => {
                  //   setRightPanelInfo({
                  //     detailType: `stop`,
                  //     detailId: `${stop.id}`,
                  //     detailTab: detailTab ?? null,
                  //   });
                  // }}
                  />
                }
              />
            </>

            <Route
              path="*"
              element={
                <Navigate
                  to={routing.PLANNER.PLAN_DETAILS.OVERVIEW.replace(`:projectId`, activeProject.id).replace(
                    `:planId`,
                    activePlan?.id ?? planId ?? ``
                  )}
                />
              }
            />
          </Routes>
        </Grid>
      </PageLayout>

      <CurrentOptimizationDialog />
      <OptimizationSnackbar />
    </>
  );
};
